import React, { useState, useMemo, useCallback } from 'react';
import {
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  Users,
  RefreshCcw,
} from 'lucide-react';
import { useBgvPendingReviews } from '../hooks/useBgv';
import { bgvService } from '../services/bgv';
import type { BgvPendingReview } from '../types/bgv';
import { cn, formatDateTime, formatNumber, formatCentsDecimal, showToast } from '../utils/helpers';
import { Badge } from '../components/ui';

// ============================================================================
// Badge helpers
// ============================================================================

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'muted';

function workflowStageBadge(stage: string | null): { variant: BadgeVariant; label: string } {
  if (!stage) return { variant: 'muted', label: 'Unknown' };
  const map: Record<string, BadgeVariant> = {
    APPROVED: 'success',
    AWAITING_REVIEW: 'warning',
    DENIED: 'error',
    POST_ADVERSE_ACTION: 'error',
    REPORT_PENDING: 'info',
    INVITATION_SENT: 'info',
    INVITATION_COMPLETED: 'info',
    EXPIRED: 'muted',
    SUSPENDED: 'muted',
    CANCELED: 'muted',
    DISPUTED: 'warning',
    PRE_ADVERSE_ACTION: 'warning',
  };
  return { variant: map[stage] || 'muted', label: stage.replace(/_/g, ' ') };
}

function paymentStatusBadge(status: string | null): { variant: BadgeVariant } {
  if (!status) return { variant: 'muted' };
  const map: Record<string, BadgeVariant> = {
    succeeded: 'success',
    pending: 'warning',
    failed: 'error',
    requires_payment_method: 'warning',
    canceled: 'muted',
  };
  return { variant: map[status] || 'muted' };
}

function adjudicationBadge(adj: string | null): { variant: BadgeVariant } {
  if (!adj) return { variant: 'muted' };
  const map: Record<string, BadgeVariant> = {
    approved: 'success',
    denied: 'error',
    pending_review: 'warning',
    engaged: 'info',
  };
  return { variant: map[adj] || 'muted' };
}

// ============================================================================
// Stat Card (reused pattern from Webhooks)
// ============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, color, bgColor, loading }) => (
  <div className="card p-4">
    <div className="flex items-center gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        {loading ? (
          <div className="h-7 w-16 bg-muted/30 rounded animate-pulse mt-0.5" />
        ) : (
          <p className="text-xl font-bold text-foreground">{value}</p>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// Detail helpers (reused pattern from Webhooks)
// ============================================================================

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="pt-2 mt-2 first:mt-0 first:pt-0">
    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">{children}</div>
  </div>
);

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">{label}</span>
      <span className="text-[10px] text-foreground text-right break-all">{value}</span>
    </div>
  );
};

// ============================================================================
// Review Confirmation Modal
// ============================================================================

interface ReviewModalProps {
  review: BgvPendingReview;
  decision: 'approved' | 'denied';
  onConfirm: (reviewNotes: string) => void;
  onCancel: () => void;
  submitting: boolean;
}

const ReviewModal: React.FC<ReviewModalProps> = ({ review, decision, onConfirm, onCancel, submitting }) => {
  const [notes, setNotes] = useState('');

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          {decision === 'approved' ? (
            <div className="p-2 rounded-xl bg-emerald-400/10">
              <CheckCircle className="h-5 w-5 text-emerald-400" />
            </div>
          ) : (
            <div className="p-2 rounded-xl bg-red-400/10">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
          )}
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              {decision === 'approved' ? 'Approve' : 'Deny'} Background Check
            </h3>
            <p className="text-xs text-muted-foreground">
              {review.user.name || review.user.email || `User #${review.userId}`}
            </p>
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1.5">
            Review Notes {decision === 'denied' && <span className="text-muted-foreground/60">(recommended for denials)</span>}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes about this decision..."
            rows={3}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
          />
        </div>

        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-primary/10 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(notes)}
            disabled={submitting}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2',
              decision === 'approved'
                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
            )}
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {decision === 'approved' ? 'Approve' : 'Deny'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Sync Checkr Action (inline in expanded row)
// ============================================================================

const SyncCheckrAction: React.FC<{
  item: BgvPendingReview;
  onSync: (item: BgvPendingReview, candidateId: string, reportId: string) => void;
  syncing: boolean;
}> = ({ item, onSync, syncing }) => {
  const [candidateId, setCandidateId] = useState(item.candidate_id || '');
  const [reportId, setReportId] = useState(item.report_id || '');

  const canSync = candidateId.trim() && reportId.trim();

  return (
    <div className="pt-2 mt-2 p-3 bg-muted/10 rounded-lg">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Sync from Checkr</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <div>
          <label className="text-[10px] text-muted-foreground block mb-0.5">Candidate ID</label>
          <input
            type="text"
            value={candidateId}
            onChange={(e) => setCandidateId(e.target.value)}
            placeholder="e.g. 492324b8179a632eba5979ca"
            className="w-full px-2 py-1 text-[11px] font-mono bg-background border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground block mb-0.5">Report ID</label>
          <input
            type="text"
            value={reportId}
            onChange={(e) => setReportId(e.target.value)}
            placeholder="e.g. 373be55201c889081e960950"
            className="w-full px-2 py-1 text-[11px] font-mono bg-background border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onSync(item, candidateId.trim(), reportId.trim())}
          disabled={syncing || !canSync}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-md bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="h-3 w-3" />}
          Sync from Checkr
        </button>
        <p className="text-[10px] text-muted-foreground/60">Fetches report &amp; candidate from Checkr, re-runs adjudication</p>
      </div>
    </div>
  );
};

// ============================================================================
// Pending Review Row
// ============================================================================

interface ReviewRowProps {
  item: BgvPendingReview;
  expanded: boolean;
  onToggle: () => void;
  onAction: (decision: 'approved' | 'denied') => void;
  onSyncCheckr: (item: BgvPendingReview, candidateId: string, reportId: string) => void;
  syncing: boolean;
}

const ReviewRow: React.FC<ReviewRowProps> = ({ item, expanded, onToggle, onAction, onSyncCheckr, syncing }) => {
  const stage = workflowStageBadge(item.workflowStage);

  return (
    <div className={cn('card p-3 cursor-pointer transition-all', expanded && 'ring-1 ring-primary/30')} onClick={onToggle}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
          <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">
            {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-foreground font-medium truncate">{item.user.name || item.user.email || `User #${item.userId}`}</p>
            <p className="text-[10px] text-muted-foreground truncate">
              {[item.user.email, item.user.phoneNumber].filter(Boolean).join(' · ') || `User ID: ${item.userId}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {item.paymentStatus && (
            <Badge variant={paymentStatusBadge(item.paymentStatus).variant} size="sm">
              {item.paymentStatus === 'succeeded' ? 'Paid' : item.paymentStatus}
            </Badge>
          )}
          {item.checkrResultRaw && (
            <Badge variant={item.checkrResultRaw === 'clear' ? 'success' : item.checkrResultRaw === 'consider' ? 'warning' : 'muted'} size="sm">
              {item.checkrResultRaw}
            </Badge>
          )}
          <Badge variant={stage.variant} size="sm">{stage.label}</Badge>
          {/* Action buttons */}
          <div className="flex items-center gap-1 ml-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onAction('approved')}
              className="px-2.5 py-1 text-[10px] font-medium rounded-md bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors"
            >
              Approve
            </button>
            <button
              onClick={() => onAction('denied')}
              className="px-2.5 py-1 text-[10px] font-medium rounded-md bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors"
            >
              Deny
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
          {/* User Info */}
          <DetailSection title="User">
            <DetailRow label="User ID" value={String(item.userId)} />
            <DetailRow label="Name" value={item.user.name} />
            <DetailRow label="Email" value={item.user.email} />
            <DetailRow label="Phone" value={item.user.phoneNumber} />
          </DetailSection>

          {/* BGV Status & Adjudication */}
          <DetailSection title="Status">
            <DetailRow label="Status" value={item.status} />
            <DetailRow label="Report Status" value={item.report_status} />
            <DetailRow label="Adjudication" value={
              item.adjudication ? (
                <Badge variant={adjudicationBadge(item.adjudication).variant} size="sm">{item.adjudication.replace(/_/g, ' ')}</Badge>
              ) : undefined
            } />
            <DetailRow label="Workflow Stage" value={
              item.workflowStage ? (
                <Badge variant={workflowStageBadge(item.workflowStage).variant} size="sm">{item.workflowStage.replace(/_/g, ' ')}</Badge>
              ) : undefined
            } />
            <DetailRow label="Recommended Status" value={item.recommendedStatusLabel} />
            <DetailRow label="Displayed Outcome" value={item.displayedOutcome} />
          </DetailSection>

          {/* Checkr IDs & Raw Data */}
          <DetailSection title="Checkr Details">
            <DetailRow label="Candidate ID" value={item.candidate_id} />
            <DetailRow label="Invitation ID" value={item.invitation_id} />
            <DetailRow label="Report ID" value={item.report_id} />
            <DetailRow label="Package" value={item.package || (item.result?.package as string)} />
            <DetailRow label="Report Status (raw)" value={item.checkrReportStatusRaw} />
            <DetailRow label="Result (raw)" value={item.checkrResultRaw} />
            <DetailRow label="Assessment (raw)" value={item.checkrAssessmentRaw} />
            <DetailRow label="Adjudication (raw)" value={item.checkrAdjudicationRaw} />
            <DetailRow label="Includes Canceled" value={item.includesCanceled ? 'Yes' : 'No'} />
            <DetailRow label="ETA Completion" value={item.etaEstimatedCompletionTime ? formatDateTime(item.etaEstimatedCompletionTime) : undefined} />
          </DetailSection>

          {/* Review Notes */}
          {item.reviewNotes && (
            <DetailSection title="Review Notes">
              <div className="col-span-full text-[10px] text-foreground bg-muted/20 rounded p-2 whitespace-pre-wrap">{item.reviewNotes}</div>
            </DetailSection>
          )}

          {/* Payment */}
          <DetailSection title="Payment">
            <DetailRow label="Payment Intent" value={item.paymentIntentId} />
            <DetailRow label="Status" value={
              item.paymentStatus ? (
                <Badge variant={paymentStatusBadge(item.paymentStatus).variant} size="sm">{item.paymentStatus}</Badge>
              ) : undefined
            } />
            <DetailRow label="Amount" value={item.paymentAmount != null ? formatCentsDecimal(item.paymentAmount) : undefined} />
            <DetailRow label="Paid At" value={item.paidAt ? formatDateTime(item.paidAt) : undefined} />
          </DetailSection>

          {/* Webhooks */}
          {(item.lastWebhookAt || item.lastWebhookType) && (
            <DetailSection title="Last Webhook">
              <DetailRow label="Type" value={item.lastWebhookType} />
              <DetailRow label="Webhook ID" value={item.lastWebhookId} />
              <DetailRow label="Received At" value={item.lastWebhookAt ? formatDateTime(item.lastWebhookAt) : undefined} />
            </DetailSection>
          )}

          {/* Checkr Report (raw result object) */}
          {item.result && (
            <DetailSection title="Checkr Report Data">
              <DetailRow label="Report ID" value={item.result.id as string} />
              <DetailRow label="Status" value={item.result.status as string} />
              <DetailRow label="Result" value={item.result.result as string} />
              <DetailRow label="Assessment" value={item.result.assessment as string} />
              <DetailRow label="Adjudication" value={item.result.adjudication as string} />
              <DetailRow label="Package" value={item.result.package as string} />
              <DetailRow label="Source" value={item.result.source as string} />
              <DetailRow label="Candidate ID" value={item.result.candidate_id as string} />
              <DetailRow label="Due Time" value={item.result.due_time ? formatDateTime(item.result.due_time as string) : undefined} />
              <DetailRow label="Est. Completion" value={item.result.estimated_completion_time ? formatDateTime(item.result.estimated_completion_time as string) : undefined} />
              <DetailRow label="Created" value={item.result.created_at ? formatDateTime(item.result.created_at as string) : undefined} />
              <DetailRow label="Completed" value={item.result.completed_at ? formatDateTime(item.result.completed_at as string) : undefined} />
              {item.result.work_locations && (item.result.work_locations as Array<{state: string; country: string}>).length > 0 && (
                <DetailRow label="Work Location" value={(item.result.work_locations as Array<{state: string; country: string}>).map(l => `${l.state}, ${l.country}`).join('; ')} />
              )}
              <DetailRow label="Includes Canceled" value={(item.result.includes_canceled as boolean) ? 'Yes' : 'No'} />
            </DetailSection>
          )}

          {/* Sync Checkr action */}
          <SyncCheckrAction item={item} onSync={onSyncCheckr} syncing={syncing} />

          {/* Links */}
          {(item.invitation_url || item.report_id) && (
            <DetailSection title="Links">
              {item.invitation_url && (
                <a
                  href={item.invitation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[10px] text-primary hover:text-primary/80 transition-colors"
                >
                  <ExternalLink className="h-3 w-3" /> View Invitation
                </a>
              )}
              {item.report_id && (
                <a
                  href={`https://dashboard.checkr.com/reports/${item.report_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[10px] text-primary hover:text-primary/80 transition-colors"
                >
                  <ExternalLink className="h-3 w-3" /> View Report
                </a>
              )}
            </DetailSection>
          )}

          {/* Full Timeline */}
          <DetailSection title="Timeline">
            <DetailRow label="Created" value={formatDateTime(item.createdAt)} />
            <DetailRow label="Updated" value={formatDateTime(item.updatedAt)} />
            <DetailRow label="Invited" value={item.invitedAt ? formatDateTime(item.invitedAt) : undefined} />
            <DetailRow label="Completed" value={item.completedAt ? formatDateTime(item.completedAt) : undefined} />
            <DetailRow label="Approved" value={item.approvedAt ? formatDateTime(item.approvedAt) : undefined} />
            <DetailRow label="Denied" value={item.deniedAt ? formatDateTime(item.deniedAt) : undefined} />
            <DetailRow label="Expired" value={item.expiredAt ? formatDateTime(item.expiredAt) : undefined} />
            <DetailRow label="Canceled" value={item.canceledAt ? formatDateTime(item.canceledAt) : undefined} />
            <DetailRow label="Suspended" value={item.suspendedAt ? formatDateTime(item.suspendedAt) : undefined} />
          </DetailSection>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Background Verification Page
// ============================================================================

const BackgroundVerification: React.FC = () => {
  const { items, loading, error, refetch } = useBgvPendingReviews();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [reviewModal, setReviewModal] = useState<{ review: BgvPendingReview; decision: 'approved' | 'denied' } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [syncingUserId, setSyncingUserId] = useState<number | null>(null);

  // Compute stats from the data
  const stats = useMemo(() => {
    const total = items.length;
    let awaitingPayment = 0;
    let inProgress = 0;
    let needsReview = 0;
    let completed = 0;

    for (const i of items) {
      const stage = i.workflowStage;
      // Terminal states first
      if (stage === 'APPROVED' || stage === 'DENIED' || stage === 'POST_ADVERSE_ACTION') {
        completed++;
      // Needs admin action
      } else if (stage === 'AWAITING_REVIEW' || stage === 'PRE_ADVERSE_ACTION' || stage === 'DISPUTED'
        || i.adjudication === 'pending_review') {
        needsReview++;
      // Checkr in progress (report pending, invitation flow)
      } else if (stage === 'REPORT_PENDING' || stage === 'INVITATION_SENT' || stage === 'INVITATION_COMPLETED'
        || (i.paymentStatus === 'succeeded' && !stage)) {
        inProgress++;
      // Everything else — awaiting payment / not started
      } else {
        awaitingPayment++;
      }
    }

    return { total, awaitingPayment, inProgress, needsReview, completed };
  }, [items]);

  const handleSyncCheckr = useCallback(async (item: BgvPendingReview, candidateId: string, reportId: string) => {
    setSyncingUserId(item.userId);
    try {
      await bgvService.syncCheckr(item.userId, { candidateId, reportId });
      showToast('success', `Checkr data synced for ${item.user.name || item.user.email || `User #${item.userId}`}`);
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sync Checkr data';
      showToast('error', message);
    } finally {
      setSyncingUserId(null);
    }
  }, [refetch]);

  const handleReview = useCallback(async (reviewNotes: string) => {
    if (!reviewModal) return;
    setSubmitting(true);
    try {
      await bgvService.reviewUser(reviewModal.review.userId, {
        decision: reviewModal.decision,
        reviewNotes: reviewNotes || undefined,
      });
      showToast(
        'success',
        `Background check ${reviewModal.decision} for ${reviewModal.review.user.name || reviewModal.review.user.email || `User #${reviewModal.review.userId}`}`
      );
      setReviewModal(null);
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit review';
      showToast('error', message);
    } finally {
      setSubmitting(false);
    }
  }, [reviewModal, refetch]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground">Background Checks</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Review and manage Checkr background verifications</p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={loading}
            className="p-2 rounded-lg hover:bg-primary/10 transition-colors"
          >
            <RefreshCw className={cn('h-4 w-4 text-muted-foreground', loading && 'animate-spin')} />
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total Records" value={formatNumber(stats.total)} icon={FileText} color="text-blue-400" bgColor="bg-blue-400/10" loading={loading} />
          <StatCard label="Awaiting Payment" value={formatNumber(stats.awaitingPayment)} icon={Clock} color="text-yellow-400" bgColor="bg-yellow-400/10" loading={loading} />
          <StatCard label="In Progress" value={formatNumber(stats.inProgress)} icon={Loader2} color="text-cyan-400" bgColor="bg-cyan-400/10" loading={loading} />
          <StatCard label="Needs Review" value={formatNumber(stats.needsReview)} icon={ShieldAlert} color="text-orange-400" bgColor="bg-orange-400/10" loading={loading} />
        </div>

        {/* Error state */}
        {error && (
          <div className="card p-4 border-red-500/30">
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-4 w-4" />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Pending Reviews Table */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">All Records</h3>
            <span className="text-xs text-muted-foreground">({formatNumber(items.length)})</span>
          </div>

          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="card p-12 text-center">
              <ShieldCheck className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No pending reviews</p>
              <p className="text-xs text-muted-foreground/60 mt-1">All background verifications have been processed</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <ReviewRow
                  key={item.id}
                  item={item}
                  expanded={expandedId === item.id}
                  onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  onAction={(decision) => setReviewModal({ review: item, decision })}
                  onSyncCheckr={handleSyncCheckr}
                  syncing={syncingUserId === item.userId}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Review confirmation modal */}
      {reviewModal && (
        <ReviewModal
          review={reviewModal.review}
          decision={reviewModal.decision}
          onConfirm={handleReview}
          onCancel={() => setReviewModal(null)}
          submitting={submitting}
        />
      )}
    </div>
  );
};

export default BackgroundVerification;
